import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Phone, 
  Clock, 
  TrendingUp, 
  Activity,
  PhoneIncoming
} from 'lucide-react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';

const StatCard = ({ title, value, icon: Icon, trend, color }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 card-hover">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-500 text-sm font-medium mb-1">{title}</p>
        <h3 className="text-3xl font-bold text-gray-900">{value}</h3>
        {trend && (
          <p className={`text-sm mt-2 ${trend.startsWith('+') ? 'text-green-600' : 'text-gray-500'}`}>
            {trend}
          </p>
        )}
      </div>
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const RecentCallCard = ({ call }) => {
  const getStatusIcon = () => {
    switch (call.call_status) {
      case 'ongoing': return <Activity className="w-4 h-4 text-blue-600 animate-pulse" />;
      case 'ended': return <PhoneIncoming className="w-4 h-4 text-green-600" />;
      default: return <Phone className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = () => {
    switch (call.call_status) {
      case 'ongoing': return <span className="badge-info">Ongoing</span>;
      case 'ended': return <span className="badge-success">Completed</span>;
      case 'analyzed': return <span className="badge-success">Analyzed</span>;
      default: return <span className="badge-warning">{call.call_status}</span>;
    }
  };

  return (
    <Link 
      to={`/calls/${call.call_id}`}
      className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 card-hover flex items-center gap-4"
    >
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
        {getStatusIcon()}
      </div>
      
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-1">
          <h4 className="font-semibold text-gray-900">
            {call.caller_name || 'Unknown Caller'}
          </h4>
          {getStatusBadge()}
        </div>
        <p className="text-gray-500 text-sm">{call.caller_number || 'No number'}</p>
      </div>
      
      <div className="text-right">
        <p className="text-gray-500 text-sm">
          {call.start_time && formatDistanceToNow(new Date(call.start_time), { addSuffix: true })}
        </p>
        {call.duration && (
          <p className="text-gray-400 text-xs mt-1">
            {Math.floor(call.duration / 60)}m {call.duration % 60}s
          </p>
        )}
      </div>
    </Link>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found, redirecting to login');
        navigate('/login');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, callsRes] = await Promise.all([
        axios.get('/api/calls/stats', { headers }),
        axios.get('/api/calls?limit=5', { headers })
      ]);
      
      setStats(statsRes.data);
      setRecentCalls(callsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      
      if (error.response?.status === 401) {
        // Token expired or invalid
        console.error('Authentication failed, redirecting to login');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={fetchDashboardData} className="btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-500">Overview of your call activity</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Calls"
          value={stats?.total || 0}
          icon={Phone}
          trend="+12% this week"
          color="bg-gradient-to-br from-blue-600 to-indigo-600"
        />
        <StatCard 
          title="Today's Calls"
          value={stats?.today || 0}
          icon={Clock}
          trend="Active now"
          color="bg-gradient-to-br from-green-600 to-teal-600"
        />
        <StatCard 
          title="Active Now"
          value={stats?.byStatus?.find(s => s.call_status === 'ongoing')?.count || 0}
          icon={Activity}
          color="bg-gradient-to-br from-amber-600 to-orange-600"
        />
        <StatCard 
          title="Completed"
          value={stats?.byStatus?.find(s => s.call_status === 'ended')?.count || 0}
          icon={TrendingUp}
          trend="+8% vs yesterday"
          color="bg-gradient-to-br from-violet-600 to-purple-600"
        />
      </div>

      {/* Recent Calls */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Recent Calls</h2>
          <Link to="/calls" className="btn-secondary text-sm">
            View All
          </Link>
        </div>
        
        <div className="space-y-4">
          {recentCalls.length > 0 ? (
            recentCalls.map((call) => (
              <RecentCallCard key={call.call_id} call={call} />
            ))
          ) : (
            <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-200">
              <Phone className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No calls yet</h3>
              <p className="text-gray-500">Your recent calls will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;